import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  Plus,
  Search,
  Upload,
  Filter,
  SortAsc,
  SortDesc,
  FileText,
  FolderPlus,
  FolderMinus,
  Loader2,
  X,
  BookOpen,
  Copy,
  ChevronLeft,
  ChevronRight,
  Trash2,
  CheckSquare,
  Square,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SourceGridSkeleton } from '@/components/ui/page-skeletons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { FolderTree } from '@/features/sources/components/FolderTree';
import { BreadcrumbNav } from '@/features/sources/components/BreadcrumbNav';
import { SourceCard } from '@/features/sources/components/SourceCard';
import { DeduplicatePanel } from '@/features/sources/components/DeduplicatePanel';
import { SourceUploader } from '@/features/sources/components/SourceUploader';
import { SourceEditDialog } from '@/features/sources/components/SourceEditDialog';
import { GenericImporter } from '@/features/import/components/GenericImporter';
import { SpreadsheetImporter } from '@/features/import/components/SpreadsheetImporter';
import { useQueryClient } from '@tanstack/react-query';
import { usePaginatedSources, useDeleteSource, useAddSourceToFolder, useMoveSourceToFolder, useRemoveSourceFromFolder, useBatchDeleteSources, useBatchAddToFolder, useBatchRemoveFromFolder } from '@/features/sources/hooks/useSources';
import { useCreateFolder, useFolderTree } from '@/features/sources/hooks/useFolders';
import type { SourceFilters, FolderTreeNode } from '@/features/sources/types';
import type { ExtractionStatus, FolderType } from '@/types/database';

export function Sources() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Folder selection
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [extractionStatus, setExtractionStatus] = useState<ExtractionStatus | 'all'>('all');
  const [sortBy, setSortBy] = useState<SourceFilters['sort_by']>('created_at');
  const [sortOrder, setSortOrder] = useState<SourceFilters['sort_order']>('desc');
  const [page, setPage] = useState(0);

  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [authorFilter, setAuthorFilter] = useState('');
  const [doiFilter, setDoiFilter] = useState('');
  const [journalFilter, setJournalFilter] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');

  const hasAdvancedFilters = !!(authorFilter || doiFilter || journalFilter || yearFrom || yearTo);

  // Selection state
  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<string>>(new Set());

  // Batch folder picker state
  const [showBatchFolderPicker, setShowBatchFolderPicker] = useState(false);

  // UI state
  const [showUploader, setShowUploader] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [showDeduplicate, setShowDeduplicate] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderParentId, setNewFolderParentId] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderType, setNewFolderType] = useState<FolderType>('regular');

  // Delete confirmation state
  const [deleteSourceId, setDeleteSourceId] = useState<string | null>(null);
  // Edit source state
  const [editSourceId, setEditSourceId] = useState<string | null>(null);
  // Move source state
  const [moveSourceId, setMoveSourceId] = useState<string | null>(null);

  // Drag-and-drop state
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Data hooks
  const { data: paginatedResult, isLoading } = usePaginatedSources({
    folder_id: selectedFolderId,
    search: search || undefined,
    extraction_status: extractionStatus === 'all' ? null : extractionStatus,
    sort_by: sortBy,
    sort_order: sortOrder,
    page,
    author: authorFilter || undefined,
    doi: doiFilter || undefined,
    journal: journalFilter || undefined,
    year_from: yearFrom ? parseInt(yearFrom, 10) : undefined,
    year_to: yearTo ? parseInt(yearTo, 10) : undefined,
  });
  const sources = paginatedResult?.items;
  const totalCount = paginatedResult?.totalCount ?? 0;
  const deleteSource = useDeleteSource();
  const addSourceToFolder = useAddSourceToFolder();
  const moveSourceToFolder = useMoveSourceToFolder();
  const removeSourceFromFolder = useRemoveSourceFromFolder();
  const batchDeleteSources = useBatchDeleteSources();
  const batchAddToFolder = useBatchAddToFolder();
  const batchRemoveFromFolder = useBatchRemoveFromFolder();
  const createFolder = useCreateFolder();
  const { data: folderTree } = useFolderTree();

  const handleSelectFolder = useCallback((folderId: string | null) => {
    setSelectedFolderId(folderId);
    setPage(0);
  }, []);

  const handleCreateFolder = useCallback((parentId: string | null) => {
    setNewFolderParentId(parentId);
    setNewFolderName('');
    setNewFolderType('regular');
    setShowCreateFolder(true);
  }, []);

  const handleSubmitFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await createFolder.mutateAsync({
        name: newFolderName.trim(),
        description: null,
        parent_id: newFolderParentId,
        folder_type: newFolderType,
        color: null,
      });
      setShowCreateFolder(false);
      toast.success('Folder created');
    } catch {
      toast.error('Failed to create folder');
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    setDeleteSourceId(sourceId);
  };

  const confirmDeleteSource = async () => {
    if (!deleteSourceId) return;
    try {
      await deleteSource.mutateAsync(deleteSourceId);
      toast.success('Source deleted');
    } catch {
      toast.error('Failed to delete source');
    } finally {
      setDeleteSourceId(null);
    }
  };

  const handleEdit = (sourceId: string) => {
    setEditSourceId(sourceId);
  };

  const handleMove = (sourceId: string) => {
    setMoveSourceId(sourceId);
  };

  const handleAddToFolder = async (folderId: string) => {
    if (!moveSourceId) return;
    try {
      await addSourceToFolder.mutateAsync({ sourceId: moveSourceId, folderId });
      toast.success('Source added to folder');
    } catch {
      toast.error('Failed to add source to folder');
    } finally {
      setMoveSourceId(null);
    }
  };

  const handleMoveToFolder = async (folderId: string) => {
    if (!moveSourceId) return;
    try {
      await moveSourceToFolder.mutateAsync({ sourceId: moveSourceId, folderId });
    } catch {
      toast.error('Failed to move source');
    } finally {
      setMoveSourceId(null);
    }
  };

  const handleRemoveFromFolder = async (sourceId: string) => {
    if (!selectedFolderId) return;
    try {
      await removeSourceFromFolder.mutateAsync({ sourceId, folderId: selectedFolderId });
    } catch {
      toast.error('Failed to remove source from folder');
    }
  };

  const handleChat = (sourceId: string) => {
    navigate(`/chat/${sourceId}`);
  };

  const handleEvaluate = (sourceId: string) => {
    navigate(`/evaluate?source=${sourceId}`);
  };

  const handleToggleSelect = useCallback((sourceId: string) => {
    setSelectedSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(sourceId)) {
        next.delete(sourceId);
      } else {
        next.add(sourceId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!sources) return;
    const allIds = sources.map((s) => s.id);
    const allSelected = allIds.every((id) => selectedSourceIds.has(id));
    if (allSelected) {
      setSelectedSourceIds(new Set());
    } else {
      setSelectedSourceIds(new Set(allIds));
    }
  }, [sources, selectedSourceIds]);

  const handleBatchDelete = async () => {
    if (selectedSourceIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedSourceIds.size} source(s)? This cannot be undone.`)) return;
    await batchDeleteSources.mutateAsync(Array.from(selectedSourceIds));
    setSelectedSourceIds(new Set());
  };

  const handleBatchAddToFolder = async (folderId: string) => {
    if (selectedSourceIds.size === 0) return;
    await batchAddToFolder.mutateAsync({ sourceIds: Array.from(selectedSourceIds), folderId });
    setSelectedSourceIds(new Set());
    setShowBatchFolderPicker(false);
  };

  const handleBatchRemoveFromFolder = async () => {
    if (selectedSourceIds.size === 0 || !selectedFolderId) return;
    await batchRemoveFromFolder.mutateAsync({ sourceIds: Array.from(selectedSourceIds), folderId: selectedFolderId });
    setSelectedSourceIds(new Set());
  };

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    const sourceId = active.id as string;
    const targetFolderId = over.id as string;

    // Don't do anything if dropping on the same area
    if (targetFolderId === 'all-sources') return;

    try {
      await addSourceToFolder.mutateAsync({ sourceId, folderId: targetFolderId });
    } catch {
      // Error handled by hook
    }
  }, [addSourceToFolder]);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(0);
  };

  const sourceCount = sources?.length ?? 0;
  const totalPages = Math.ceil(totalCount / 50);

  const activeDragSource = activeDragId ? sources?.find((s) => s.id === activeDragId) : null;

  return (
    <DndContext sensors={dndSensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
    <div className="flex h-full">
      {/* Left sidebar - Folder tree (desktop) */}
      <aside className="hidden w-64 shrink-0 border-r border-slate-200 bg-white lg:block">
        <div className="flex h-full flex-col overflow-y-auto">
          <FolderTree
            selectedFolderId={selectedFolderId}
            onSelectFolder={handleSelectFolder}
            onCreateFolder={handleCreateFolder}
          />
        </div>
      </aside>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <div className="border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              {/* Mobile folder tree trigger */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="lg:hidden">
                    <FolderPlus className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0">
                  <SheetHeader className="px-4 pt-4">
                    <SheetTitle>Folders</SheetTitle>
                    <SheetDescription>Browse and manage your folders</SheetDescription>
                  </SheetHeader>
                  <FolderTree
                    selectedFolderId={selectedFolderId}
                    onSelectFolder={(id) => {
                      handleSelectFolder(id);
                    }}
                    onCreateFolder={handleCreateFolder}
                  />
                </SheetContent>
              </Sheet>

              <BreadcrumbNav
                folderId={selectedFolderId ?? undefined}
                onNavigate={handleSelectFolder}
                className="min-w-0"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={showUploader ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setShowUploader((prev) => !prev);
                  setShowImporter(false);
                  setShowDeduplicate(false);
                }}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </Button>
              <Button
                variant={showImporter ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setShowImporter((prev) => !prev);
                  setShowUploader(false);
                  setShowDeduplicate(false);
                }}
              >
                <BookOpen className="mr-2 h-4 w-4" />
                Import
              </Button>
              <Button
                variant={showDeduplicate ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setShowDeduplicate((prev) => !prev);
                  setShowUploader(false);
                  setShowImporter(false);
                }}
              >
                <Copy className="mr-2 h-4 w-4" />
                Duplicates
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="hidden sm:flex"
                onClick={() => handleCreateFolder(selectedFolderId)}
              >
                <Plus className="mr-2 h-4 w-4" />
                New Folder
              </Button>
            </div>
          </div>

          {/* Search and filters */}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search sources…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-9"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <Select
              value={extractionStatus}
              onValueChange={(v) => { setExtractionStatus(v as ExtractionStatus | 'all'); setPage(0); }}
            >
              <SelectTrigger className="w-[150px]">
                <Filter className="mr-2 h-3.5 w-3.5 text-slate-400" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="completed">Extracted</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(v) => { setSortBy(v as SourceFilters['sort_by']); setPage(0); }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Date added</SelectItem>
                <SelectItem value="title">Title</SelectItem>
                <SelectItem value="file_name">File name</SelectItem>
                <SelectItem value="publication_year">Year</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="ghost" size="icon" onClick={toggleSortOrder} className="h-9 w-9">
              {sortOrder === 'asc' ? (
                <SortAsc className="h-4 w-4" />
              ) : (
                <SortDesc className="h-4 w-4" />
              )}
            </Button>

            <Button
              variant={showAdvancedFilters || hasAdvancedFilters ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowAdvancedFilters((prev) => !prev)}
            >
              <Filter className="mr-1 h-3.5 w-3.5" />
              Advanced
              {hasAdvancedFilters && (
                <Badge variant="secondary" className="ml-1.5 h-4 min-w-[16px] px-1 text-[10px]">
                  !
                </Badge>
              )}
            </Button>
          </div>

          {/* Advanced filters panel */}
          {showAdvancedFilters && (
            <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="min-w-[150px] flex-1">
                <Label htmlFor="filter-author" className="text-xs text-slate-500">Author</Label>
                <Input
                  id="filter-author"
                  placeholder="e.g. Smith"
                  value={authorFilter}
                  onChange={(e) => { setAuthorFilter(e.target.value); setPage(0); }}
                  className="h-8 text-sm"
                />
              </div>
              <div className="min-w-[150px] flex-1">
                <Label htmlFor="filter-doi" className="text-xs text-slate-500">DOI</Label>
                <Input
                  id="filter-doi"
                  placeholder="e.g. 10.1016/..."
                  value={doiFilter}
                  onChange={(e) => { setDoiFilter(e.target.value); setPage(0); }}
                  className="h-8 text-sm"
                />
              </div>
              <div className="min-w-[150px] flex-1">
                <Label htmlFor="filter-journal" className="text-xs text-slate-500">Journal</Label>
                <Input
                  id="filter-journal"
                  placeholder="e.g. Nature"
                  value={journalFilter}
                  onChange={(e) => { setJournalFilter(e.target.value); setPage(0); }}
                  className="h-8 text-sm"
                />
              </div>
              <div className="w-[90px]">
                <Label htmlFor="filter-year-from" className="text-xs text-slate-500">Year from</Label>
                <Input
                  id="filter-year-from"
                  type="number"
                  placeholder="2020"
                  value={yearFrom}
                  onChange={(e) => { setYearFrom(e.target.value); setPage(0); }}
                  className="h-8 text-sm"
                />
              </div>
              <div className="w-[90px]">
                <Label htmlFor="filter-year-to" className="text-xs text-slate-500">Year to</Label>
                <Input
                  id="filter-year-to"
                  type="number"
                  placeholder="2025"
                  value={yearTo}
                  onChange={(e) => { setYearTo(e.target.value); setPage(0); }}
                  className="h-8 text-sm"
                />
              </div>
              {hasAdvancedFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => {
                    setAuthorFilter('');
                    setDoiFilter('');
                    setJournalFilter('');
                    setYearFrom('');
                    setYearTo('');
                    setPage(0);
                  }}
                >
                  <X className="mr-1 h-3 w-3" />
                  Clear
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Upload area */}
          {showUploader && (
            <div className="mb-6">
              <SourceUploader
                folderId={selectedFolderId}
                onUploadComplete={() => setShowUploader(false)}
              />
              <Separator className="mt-6" />
            </div>
          )}

          {/* Import area */}
          {showImporter && (
            <div className="mb-6">
              <Tabs defaultValue="ris" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="ris">RIS (.ris)</TabsTrigger>
                  <TabsTrigger value="bib">BibTeX (.bib)</TabsTrigger>
                  <TabsTrigger value="pubmed">PubMed (.txt)</TabsTrigger>
                  <TabsTrigger value="csv">CSV / Excel</TabsTrigger>
                </TabsList>
                <TabsContent value="ris">
                  <GenericImporter
                    format="ris"
                    folderId={selectedFolderId}
                    onCancel={() => setShowImporter(false)}
                  />
                </TabsContent>
                <TabsContent value="bib">
                  <GenericImporter
                    format="bib"
                    folderId={selectedFolderId}
                    onCancel={() => setShowImporter(false)}
                  />
                </TabsContent>
                <TabsContent value="pubmed">
                  <GenericImporter
                    format="pubmed"
                    folderId={selectedFolderId}
                    onCancel={() => setShowImporter(false)}
                  />
                </TabsContent>
                <TabsContent value="csv">
                  <SpreadsheetImporter
                    folderId={selectedFolderId}
                    onCancel={() => setShowImporter(false)}
                  />
                </TabsContent>
              </Tabs>
              <Separator className="mt-6" />
            </div>
          )}

          {/* Deduplicate panel */}
          {showDeduplicate && (
            <div className="mb-6">
              <DeduplicatePanel
                folderId={selectedFolderId}
                onResolved={() => queryClient.invalidateQueries({ queryKey: ['sources'] })}
              />
              <Separator className="mt-6" />
            </div>
          )}

          {/* Source count + batch actions */}
          {!isLoading && (
            <div className="mb-4 flex items-center gap-3 flex-wrap">
              <p className="text-sm text-slate-500">
                {totalCount} source{totalCount !== 1 ? 's' : ''}
                {search && ` matching "${search}"`}
                {totalPages > 1 && ` — page ${page + 1} of ${totalPages}`}
              </p>

              {sourceCount > 0 && (
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-indigo-600"
                >
                  {sources?.every((s) => selectedSourceIds.has(s.id)) ? (
                    <CheckSquare className="h-3.5 w-3.5" />
                  ) : (
                    <Square className="h-3.5 w-3.5" />
                  )}
                  {sources?.every((s) => selectedSourceIds.has(s.id)) ? 'Deselect all' : 'Select all'}
                </button>
              )}

              {selectedSourceIds.size > 0 && (
                <div className="flex items-center gap-2 ml-auto rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5">
                  <span className="text-xs font-medium text-indigo-700">
                    {selectedSourceIds.size} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-indigo-700 hover:text-indigo-900"
                    onClick={() => setShowBatchFolderPicker(true)}
                  >
                    <FolderPlus className="mr-1 h-3 w-3" />
                    Add to folder
                  </Button>
                  {selectedFolderId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-indigo-700 hover:text-indigo-900"
                      onClick={handleBatchRemoveFromFolder}
                      disabled={batchRemoveFromFolder.isPending}
                    >
                      <FolderMinus className="mr-1 h-3 w-3" />
                      Remove from folder
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-rose-600 hover:text-rose-800"
                    onClick={handleBatchDelete}
                    disabled={batchDeleteSources.isPending}
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    Delete
                  </Button>
                  <button
                    type="button"
                    onClick={() => setSelectedSourceIds(new Set())}
                    className="ml-1 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Loading state */}
          {isLoading && <SourceGridSkeleton count={6} />}

          {/* Empty state */}
          {!isLoading && sourceCount === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                <FileText className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="mt-4 text-sm font-medium text-slate-900">No sources yet</h3>
              <p className="mt-1 text-sm text-slate-500">
                {search
                  ? 'No sources match your search. Try different keywords.'
                  : 'Upload your first document to get started.'}
              </p>
              {!search && (
                <Button
                  className="mt-4"
                  onClick={() => setShowUploader(true)}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Sources
                </Button>
              )}
            </div>
          )}

          {/* Source grid */}
          {!isLoading && sourceCount > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {sources?.map((source) => (
                <DraggableSourceCard key={source.id} source={source}>
                  <SourceCard
                    source={source}
                    isSelected={selectedSourceIds.has(source.id)}
                    onSelect={handleToggleSelect}
                    onEdit={handleEdit}
                    onMove={handleMove}
                    onDelete={handleDeleteSource}
                    onChat={handleChat}
                    onEvaluate={handleEvaluate}
                    onRemoveFromFolder={selectedFolderId ? handleRemoveFromFolder : undefined}
                  />
                </DraggableSourceCard>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-slate-600">
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Create folder dialog */}
      <Dialog open={showCreateFolder} onOpenChange={setShowCreateFolder}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
            <DialogDescription>
              Organize your sources into folders for better management.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Folder name</Label>
              <Input
                id="folder-name"
                placeholder="e.g. Systematic Review 2024"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmitFolder();
                }}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="folder-type">Type</Label>
              <Select
                value={newFolderType}
                onValueChange={(v) => setNewFolderType(v as FolderType)}
              >
                <SelectTrigger id="folder-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="import">Import</SelectItem>
                  <SelectItem value="result">Results</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateFolder(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitFolder}
              disabled={!newFolderName.trim() || createFolder.isPending}
            >
              {createFolder.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog (BUG#9) */}
      <Dialog open={!!deleteSourceId} onOpenChange={(open) => !open && setDeleteSourceId(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete source?</DialogTitle>
            <DialogDescription>
              This will permanently delete the source and its extracted content. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSourceId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteSource}
              disabled={deleteSource.isPending}
            >
              {deleteSource.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit source dialog — full metadata editor */}
      <SourceEditDialog
        source={sources?.find((s) => s.id === editSourceId) ?? null}
        open={!!editSourceId}
        onOpenChange={(open) => { if (!open) setEditSourceId(null); }}
      />

      {/* Batch add to folder dialog */}
      <Dialog open={showBatchFolderPicker} onOpenChange={setShowBatchFolderPicker}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add {selectedSourceIds.size} source{selectedSourceIds.size > 1 ? 's' : ''} to folder</DialogTitle>
            <DialogDescription>Select a destination folder.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-60 overflow-y-auto py-2">
            {folderTree?.length ? (
              folderTree.map((node) => (
                <BatchFolderPickerItem
                  key={node.id}
                  node={node}
                  depth={0}
                  onSelect={handleBatchAddToFolder}
                />
              ))
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">No folders available.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchFolderPicker(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Move source to folder dialog */}
      <Dialog open={!!moveSourceId} onOpenChange={(open) => !open && setMoveSourceId(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add or Move to Folder</DialogTitle>
            <DialogDescription>
              <strong>Add</strong> keeps existing folders. <strong>Move</strong> removes from all other folders first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-60 overflow-y-auto py-2">
            {folderTree?.length ? (
              folderTree.map((node) => (
                <FolderPickerItem
                  key={node.id}
                  node={node}
                  depth={0}
                  onAdd={handleAddToFolder}
                  onMove={handleMoveToFolder}
                />
              ))
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">No folders available. Create one first.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveSourceId(null)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drag overlay */}
      <DragOverlay>
        {activeDragSource ? (
          <div className="rounded-lg border border-indigo-300 bg-white px-3 py-2 shadow-lg opacity-80">
            <p className="text-sm font-medium text-slate-900 truncate max-w-[200px]">
              {activeDragSource.title ?? activeDragSource.file_name}
            </p>
          </div>
        ) : null}
      </DragOverlay>
    </div>
    </DndContext>
  );
}

// ---- Draggable source card wrapper ----

function DraggableSourceCard({
  source,
  children,
}: {
  source: { id: string };
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: source.id,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      {children}
    </div>
  );
}

// ---- Recursive folder picker for move dialog ----

function FolderPickerItem({
  node,
  depth,
  onAdd,
  onMove,
}: {
  node: FolderTreeNode;
  depth: number;
  onAdd: (folderId: string) => void;
  onMove: (folderId: string) => void;
}) {
  return (
    <>
      <div
        className="flex w-full items-center gap-2 rounded-md py-1.5 text-sm hover:bg-slate-50"
        style={{ paddingLeft: depth * 16 + 12, paddingRight: 8 }}
      >
        <FolderPlus className="h-4 w-4 text-slate-400 shrink-0" />
        <span className="truncate flex-1">{node.name}</span>
        {node.source_count > 0 && (
          <span className="text-xs text-slate-400 shrink-0">{node.source_count}</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs shrink-0"
          onClick={() => onAdd(node.id)}
        >
          Add
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs shrink-0"
          onClick={() => onMove(node.id)}
        >
          Move
        </Button>
      </div>
      {node.children.map((child) => (
        <FolderPickerItem
          key={child.id}
          node={child}
          depth={depth + 1}
          onAdd={onAdd}
          onMove={onMove}
        />
      ))}
    </>
  );
}

// ---- Simple folder picker for batch operations ----

function BatchFolderPickerItem({
  node,
  depth,
  onSelect,
}: {
  node: FolderTreeNode;
  depth: number;
  onSelect: (folderId: string) => void;
}) {
  return (
    <>
      <button
        type="button"
        onClick={() => onSelect(node.id)}
        className="flex w-full items-center gap-2 rounded-md py-2 text-sm hover:bg-slate-100 text-left"
        style={{ paddingLeft: depth * 16 + 12 }}
      >
        <FolderPlus className="h-4 w-4 text-slate-400 shrink-0" />
        <span className="truncate">{node.name}</span>
        {node.source_count > 0 && (
          <span className="ml-auto text-xs text-slate-400">{node.source_count}</span>
        )}
      </button>
      {node.children.map((child) => (
        <BatchFolderPickerItem
          key={child.id}
          node={child}
          depth={depth + 1}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}
